import { Box, styled } from '@mui/material';

import { useDisplayAspectRatio } from '#shared';

import {
  LayoutManagerToolbar,
  Region,
  useLayoutManagerConfig,
} from '../../LayoutManager';
import { blockSelectors, useSelectRegionsActive } from '../../slices';
import { useLayoutEditing, PREVIEW_STYLE_SLOTS } from '../model';

const PREFIX = 'TSMED-PreviewContainer';

export const previewContainerClasses = {
  root: `${PREFIX}-root`,
  devicesBox: `${PREFIX}-devicesBox`,
  previewFrame: `${PREFIX}-previewFrame`,
  previewContent: `${PREFIX}-previewContent`,
  regionWrapper: `${PREFIX}-regionWrapper`,
  regionInactive: `${PREFIX}-regionInactive`,
  regionHighlight: `${PREFIX}-regionHighlight`,
  activeRegionBorder: `${PREFIX}-activeRegionBorder`,
} as const;

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
})(({ theme: { spacing, palette } }) => ({
  width: '100%',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden', // Prevent any overflow from container

  // Block Carousel auto-scrolling and interactions in preview mode
  // This prevents widgets from triggering unnecessary animations during layout editing
  '& .TSMED-Carousel-carouselContent': {
    overflow: 'hidden !important', // Disable scrolling
    touchAction: 'none', // Disable touch gestures
  },
  '& .TSMED-Carousel-carousel': {
    pointerEvents: 'none', // Block mouse interactions on carousel
  },

  [`& .${previewContainerClasses.devicesBox}`]: {
    margin: '0 auto',
    marginTop: spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    position: 'absolute',
    top: 0,
  },

  [`& .${previewContainerClasses.previewFrame}`]: {
    maxWidth: '100%',
    borderRadius: spacing(1),
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    padding: spacing(2),
    container: 'preview-frame / size',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&.active-viewport': {
      borderColor: palette.primary.main,
      borderWidth: '5px',
    },
  },

  [`& .${previewContainerClasses.previewContent}`]: {
    display: 'grid',
    gap: spacing(1),
    padding: spacing(1),
    position: 'relative',
    zoom: 'calc(min(100cqw / 100vw, 100cqh / 100vh))',
    width: '100%',
    height: '100%',
  },

  [`& .${previewContainerClasses.regionWrapper}`]: {
    position: 'relative',
    transition:
      'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    borderRadius: spacing(1),
  },

  [`& .${previewContainerClasses.regionInactive}`]: {
    opacity: 0.25,
    filter: 'grayscale(0.8) brightness(0.7)',
    pointerEvents: 'none',
  },

  [`& .${previewContainerClasses.regionHighlight}`]: {
    opacity: 1,
    filter: 'none',
  },

  [`& .${previewContainerClasses.activeRegionBorder}`]: {
    position: 'absolute',
    height: '100%',
    border: `3px solid ${palette.primary.main}`,
    borderRadius: spacing(1),
    pointerEvents: 'none',
    zIndex: 5,
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `0 0 16px ${palette.primary.main}80`,
  },
}));

export const PreviewContainer = () => {
  const allRegions = useSelectRegionsActive();
  const aspectRatio = useDisplayAspectRatio();
  const hasNewCasesAside = blockSelectors.useSelectNewCasesBlocksVisible();
  const centerBonusPx = hasNewCasesAside ? 300 : 0;
  const { mode } = useLayoutEditing();

  const { gridTemplateColumns, gridTemplateAreas } = useLayoutManagerConfig(
    allRegions.map(r => r.id),
    centerBonusPx,
  );

  return (
    <Root>
      <Box className={previewContainerClasses.devicesBox}>
        <LayoutManagerToolbar />
      </Box>

      <Box
        className={previewContainerClasses.previewFrame}
        sx={{
          aspectRatio: `${aspectRatio}`,
          width: `min(100%, calc((100vh - 200px) * ${aspectRatio}))`,
        }}
      >
        <Box
          className={previewContainerClasses.previewContent}
          sx={{
            gridTemplateColumns,
            gridTemplateAreas,
          }}
        >
          {allRegions.map(region => (
            <Box
              key={region.id}
              className={previewContainerClasses.regionWrapper}
              sx={{ gridArea: region.id }}
            >
              <Region
                region={region.id}
                area={region.id}
                mode={mode}
                styleSlots={PREVIEW_STYLE_SLOTS}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </Root>
  );
};
